(() => {
  const key = 'cista_language_choice_v1';
  const guardKey = 'cista_language_redirect_v1';
  const validLanguages = ['en', 'de', 'fr', 'es', 'it'];
  const lifetime = 365 * 24 * 60 * 60 * 1000;
  let memoryChoice = null;

  function parseChoice(value) {
    try {
      const choice = JSON.parse(value);
      return validLanguages.includes(choice.language) && choice.expires > Date.now() ? choice.language : null;
    } catch { return null; }
  }

  function read() {
    if (memoryChoice) return memoryChoice;
    try {
      const value = document.cookie.split('; ').find((item) => item.startsWith(`${key}=`));
      const choice = value && parseChoice(decodeURIComponent(value.slice(key.length + 1)));
      if (choice) return choice;
    } catch {}
    try { return parseChoice(localStorage.getItem(key)); } catch { return null; }
  }

  function remember(language) {
    if (!validLanguages.includes(language)) return;
    memoryChoice = language;
    const value = JSON.stringify({ language, expires: Date.now() + lifetime });
    try {
      document.cookie = `${key}=${encodeURIComponent(value)}; Max-Age=${lifetime / 1000}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    } catch {}
    try { localStorage.setItem(key, value); } catch {}
    document.dispatchEvent(new CustomEvent('cista:language-choice', { detail: { language } }));
  }

  window.CistaLanguagePreference = { remember, read };

  const configElement = document.getElementById('CistaLanguagePromptConfig');
  const dialog = document.getElementById('CistaLanguagePrompt');
  if (!configElement || !dialog || typeof dialog.showModal !== 'function') return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch { return; }
  if (config.designMode || window.Shopify?.designMode) return;

  const current = config.language.toLowerCase().split('-')[0];
  const available = config.availableLanguages.filter((code) => validLanguages.includes(code));
  const form = dialog.querySelector('form');
  const field = form?.querySelector('[name="language_code"]');
  if (!form || !field) return;
  let previousFocus = null;
  let submitting = false;
  let showTimer;

  function close() {
    clearTimeout(showTimer);
    const wasOpen = dialog.open;
    if (wasOpen) dialog.close();
    dialog.hidden = true;
    document.body.classList.remove('cista-language-prompt-open');
    if (wasOpen && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  }

  function submitLanguage(language) {
    if (submitting || !available.includes(language)) return;
    remember(language);
    if (language === current) { close(); return; }
    field.value = language;
    let returnTo = form.querySelector('[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.appendChild(returnTo);
    }
    returnTo.value = `${location.pathname}${location.search}${location.hash}`;
    submitting = true;
    dialog.setAttribute('aria-busy', 'true');
    form.requestSubmit();
  }

  function dismiss() { remember('en'); close(); }
  dialog.querySelectorAll('[data-language-dismiss]').forEach((button) => button.addEventListener('click', dismiss));
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); dismiss(); });
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismiss();
  });
  document.addEventListener('cista:language-choice', close);
  window.addEventListener('pageshow', () => { submitting = false; dialog.removeAttribute('aria-busy'); });

  if (current !== 'en') {
    try { sessionStorage.removeItem(guardKey); } catch {}
    return;
  }

  const preference = read();
  if (preference) {
    if (preference !== 'en' && available.includes(preference)) {
      try {
        const fingerprint = `${location.pathname}${location.search}|${preference}`;
        const previous = JSON.parse(sessionStorage.getItem(guardKey) || 'null');
        if (!previous || previous.page !== fingerprint || Date.now() - previous.at > 300000) {
          sessionStorage.setItem(guardKey, JSON.stringify({ page: fingerprint, at: Date.now() }));
          submitLanguage(preference);
        }
      } catch {}
    }
    return;
  }

  function languagesFor(country) {
    const map = { FR: ['fr'], BE: ['fr'], DE: ['de'], AT: ['de'], ES: ['es'], IT: ['it'], CH: ['de', 'fr', 'it'] };
    return (map[country] || []).filter((language) => available.includes(language));
  }

  function chooseCopy(languages, data) {
    const preferred = (navigator.languages || [navigator.language]).map((language) => language.toLowerCase().split('-')[0]);
    const suggested = data.suggestions?.map((suggestion) => suggestion.parts?.language?.handle?.split('-')[0]) || [];
    return [...preferred, ...suggested, ...languages].find((language) => languages.includes(language) && config.copy[language]);
  }

  function showWhenReady(deadline) {
    if (read() || submitting) return;
    const busy = document.hidden || document.querySelector('dialog[open], cart-drawer.active, .menu-drawer-container[open]') || document.body.classList.contains('cista-localization-open');
    if (busy) {
      if (Date.now() < deadline) showTimer = setTimeout(() => showWhenReady(deadline), 500);
      return;
    }
    previousFocus = document.activeElement;
    dialog.hidden = false;
    dialog.showModal();
    document.body.classList.add('cista-language-prompt-open');
    dialog.querySelector('#CistaLanguagePromptTitle')?.focus({ preventScroll: true });
  }

  async function discover() {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
      const url = `${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true&language[exclude]=en`;
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok || read()) return;
      const data = await response.json();
      if (read()) return;
      const country = data.detected_values?.country?.handle?.toUpperCase();
      const languages = languagesFor(country);
      if (!languages.length) return;
      const copyLanguage = chooseCopy(languages, data);
      const copy = config.copy[copyLanguage];
      if (!copy) return;
      const heading = dialog.querySelector('#CistaLanguagePromptTitle');
      const description = dialog.querySelector('#CistaLanguagePromptText');
      if (country === 'CH') {
        dialog.lang = 'en';
        dialog.setAttribute('data-multilingual', '');
        dialog.removeAttribute('aria-describedby');
        description.hidden = true;
        heading.classList.add('visually-hidden');
        languages.forEach((language, index) => {
          if (index > 0) {
            const separator = document.createElement('span');
            separator.setAttribute('aria-hidden', 'true');
            separator.textContent = ' / ';
            heading.appendChild(separator);
          }
          const label = document.createElement('span');
          label.lang = language;
          label.textContent = config.copy[language].language;
          heading.appendChild(label);
        });
      } else {
        dialog.lang = copyLanguage;
        heading.textContent = copy.title;
        description.textContent = copy.question;
        dialog.querySelector('.cista-language-prompt__close').setAttribute('aria-label', copy.close);
      }
      const options = dialog.querySelector('[data-language-options]');
      languages.forEach((language) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.lang = language;
        button.className = 'cista-language-prompt__choice';
        button.textContent = config.copy[language].continue;
        button.addEventListener('click', () => submitLanguage(language));
        options.appendChild(button);
      });
      showTimer = setTimeout(() => showWhenReady(Date.now() + 15000), 800);
    } catch {} finally { clearTimeout(timeout); }
  }

  discover();
})();
